import { useEffect, useRef, useState } from "react";

import { fetchContext, updateContext, type TranslateContext } from "@/lib/translate";

/**
 * What a work sends to the model (日本-102).
 *
 * **Context belongs to the library folder, because a folder is already a work.** Setup would be the
 * wrong home — it is global, and サクラノ詩 and a news article want different envelopes. One level,
 * no per-log overrides: the folder holds it, every log inside uses it, and a log with no folder
 * uses fixed defaults it cannot edit.
 *
 * **It has to be inspectable because its failure mode is invisible.** When a name comes back as a
 * common noun the reader should see at a glance that the glossary was empty, rather than re-derive
 * it — which is also why the four unbuilt tiers are *listed and greyed* rather than hidden. A panel
 * showing only what works looks complete while being a fifth of the design.
 *
 * Two ways in, one panel: the folder's `⋯` in the library, and `文脈 ▾` on any translation.
 */

/**
 * The bounds the server enforces (`MIN_PRIOR_DEPTH` / `MAX_PRIOR_DEPTH`), restated so the control
 * refuses what the route would refuse. The server is still the authority — it answers a 400 with
 * prose — but a number field that can submit a value it knows is impossible is a control that
 * lies about its own range.
 */
const MIN_DEPTH = 1;
const MAX_DEPTH = 30;

/** The `prior` tier's key. The one row this panel can actually toggle. */
const PRIOR = "prior";

/**
 * The panel as it will look if the save lands.
 *
 * **The tier row, not just the top-level pair.** The checkbox renders from `tiers[…].enabled`, so
 * patching `priorEnabled` alone was an optimistic update nothing on screen read — the checkbox sat
 * on its old value for the whole round trip, which is precisely what the optimism is for.
 *
 * The depth is deliberately *not* patched into the label here: the label is `Previous N sentences`
 * and the server is what renders it, so rewriting it locally would be the drift `service.save`
 * answers with the whole view to prevent. A number field's blur is not a "feels broken" case.
 */
function optimistic(
  context: TranslateContext,
  body: { priorEnabled?: boolean; priorDepth?: number },
): TranslateContext {
  const next = { ...context, ...body };
  if (body.priorEnabled === undefined) return next;
  return {
    ...next,
    tiers: next.tiers.map((tier) =>
      tier.key === PRIOR ? { ...tier, enabled: body.priorEnabled ?? tier.enabled } : tier,
    ),
  };
}

export interface ContextPanelProps {
  /** Where to look the envelope up. A sentence is the only scope that can price it. */
  scope: { sentenceId?: string; logId?: string; folderId?: string };
  onClose: () => void;
  /**
   * The control this panel hangs off, when that control is a **toggle**.
   *
   * Without it the trigger is a one-way switch: `mousedown` on it is an outside click and closes
   * the panel, and the `click` that follows re-opens it from the state the close just wrote. The
   * panel is never closable from the button that opened it. `SentenceMenu` does not need this
   * because its trigger sits inside its own `root`; the `context ▾` button lives in `Translation`
   * and cannot.
   *
   * Omit it where the trigger is not a toggle — the library's `⋯ → Context…` menu item is gone by
   * the time the panel is open, and closing on a click there is right.
   */
  anchor?: React.RefObject<HTMLElement | null>;
  /** Anchors the popover to whichever control opened it. */
  className?: string;
}

export function ContextPanel({ scope, onClose, anchor, className = "" }: ContextPanelProps) {
  const [context, setContext] = useState<TranslateContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetchContext(scope)
      .then((loaded) => {
        if (!cancelled) setContext(loaded);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : "読み込めませんでした。");
      });
    return () => {
      cancelled = true;
    };
    // The scope object is rebuilt by the parent on every render; its *values* are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.sentenceId, scope.logId, scope.folderId]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (root.current?.contains(target) || anchor?.current?.contains(target)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // Marked answered, so Read's own Escape ladder does not also fire on the keystroke that
      // closed this.
      event.preventDefault();
      onClose();
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchor]);

  const save = (body: { priorEnabled?: boolean; priorDepth?: number }) => {
    if (!context?.folderId || !context.editable) return;
    // Optimistic for the toggle's sake — a checkbox that waits on a round trip feels broken — but
    // the response replaces the whole panel, because the depth is *in* the row's label and the
    // server is what renders it.
    const previous = context;
    setError(null);
    setContext(optimistic(context, body));
    updateContext(context.folderId, body)
      .then(setContext)
      .catch((cause: unknown) => {
        // **Put it back.** A refused save that left the optimistic value on screen would have the
        // panel describing an envelope that was never written — and the next translation would
        // quietly disagree with it. The 400 on an out-of-range depth is a real, reachable answer,
        // not a hypothetical.
        setContext(previous);
        setError(cause instanceof Error ? cause.message : "保存できませんでした。");
      });
  };

  return (
    <div
      ref={root}
      onClick={(event) => event.stopPropagation()}
      // **Every phase the host row handles, not just the one somebody noticed.** In the library
      // this panel renders inside a `role="button"` folder row that owns click, dblclick and
      // keydown — so double-clicking the depth field to select it started an inline folder rename
      // over the panel, and Enter in that same field toggled the folder instead of committing.
      onDoubleClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        // Escape is the exception and has to keep going: this panel's own closer is a WINDOW
        // listener, and React's `stopPropagation` stops the native event too — so swallowing it
        // here would leave Escape unable to close the thing it is aimed at.
        if (event.key !== "Escape") event.stopPropagation();
      }}
      // The panel opens inside a sentence row, which is draggable so it can be moved to another
      // log. Without this, dragging to select the depth field picks the sentence up.
      draggable={false}
      onDragStart={(event) => event.stopPropagation()}
      style={{ zIndex: "var(--z-panel)" }}
      className={`w-[400px] rounded-[var(--r-3)] border border-line bg-panel p-4 shadow-[var(--sh-popover)] ${className}`}
    >
      {error && <p className="text-[14px]/[1.5] text-accent-text">{error}</p>}
      {!context && !error && <p className="text-[14px]/[1.5] text-ink-3">読み込み中…</p>}

      {context && (
        <>
          <header className="flex items-baseline justify-between gap-3">
            <h2 className="flex items-baseline gap-2 text-[16px]/[1.4] text-ink">
              <span aria-hidden className="font-jp text-ink-3">
                冊
              </span>
              文脈
              {context.folderName && (
                <span className="text-ink-3">· {context.folderName}</span>
              )}
            </h2>
            <span className="shrink-0 font-mono text-[14px]/[1.4] text-ink-3">
              {context.totalTokens === null ? "—" : `~${context.totalTokens.toLocaleString()} tok`}
            </span>
          </header>

          <p className="mt-[2px] text-[14px]/[1.5] text-ink-4">
            {context.editable
              ? `フォルダ内の${context.logCount}ログすべてに適用`
              : // The read-only case, and it says where the setting lives rather than only that it
                // is off — otherwise a greyed panel reads as a broken one.
                "このログはフォルダに入っていません。文脈はフォルダごとに設定します。"}
          </p>

          {!context.configured && (
            <p className="mt-2 rounded-[var(--r-2)] bg-[color:var(--accent-ground)] px-2 py-1 text-[14px]/[1.5] text-[color:var(--ink-3)]">
              このデプロイでは翻訳が設定されていません。
            </p>
          )}

          <ul className="mt-3 flex flex-col">
            {context.tiers.map((tier) => (
              <li
                key={tier.key}
                className="flex items-center gap-3 border-t border-line-soft py-[7px] first:border-t-0"
              >
                <input
                  type="checkbox"
                  checked={tier.enabled}
                  disabled={!tier.available || !context.editable}
                  onChange={(event) => save({ priorEnabled: event.target.checked })}
                  aria-label={tier.label}
                  className="h-[13px] w-[13px] shrink-0 accent-[color:var(--accent)]"
                />
                <span
                  className="flex-1 text-[14px]/[1.4]"
                  style={{ color: tier.available ? "var(--ink-2)" : "var(--ink-off)" }}
                >
                  {tier.label}
                  {tier.note && (
                    <span className="ml-2 font-mono text-[14px] text-ink-4">{tier.note}</span>
                  )}
                </span>
                {/* Items AND tokens, and they are different absences: `—` for a tier that cannot be
                    counted from here, `—` again for a panel opened without a sentence to price.
                    Never `0`, which would claim the tier is empty. */}
                <span className="shrink-0 font-mono text-[14px]/[1.4] text-ink-3">
                  {tier.items === null ? "—" : tier.items.toLocaleString()}
                  <span className="text-ink-4">
                    {" · "}
                    {tier.tokens === null ? "—" : `~${tier.tokens.toLocaleString()} tok`}
                  </span>
                </span>
              </li>
            ))}
          </ul>

          {context.editable && (
            <label className="mt-3 flex items-center gap-2 text-[14px]/[1.5] text-ink-3">
              文脈の文数
              <input
                type="number"
                min={MIN_DEPTH}
                max={MAX_DEPTH}
                // Uncontrolled so typing is not a render per keystroke, but **keyed on the saved
                // value** so the server's answer (or a reverted failure) puts the box back in step
                // with the panel. Without the key a refused save left the field holding whatever
                // was typed, for ever.
                key={context.priorDepth}
                defaultValue={context.priorDepth}
                onBlur={(event) => {
                  const raw = event.target.value.trim();
                  const depth = Number(raw);
                  // **An empty field is not a depth of zero.** `Number("")` is `0` and
                  // `Number.isInteger(0)` is true, so clearing the box used to PATCH a depth the
                  // server refuses — a 400 for a gesture that means "never mind". Anything the
                  // control itself would not have produced puts the saved value back instead.
                  if (!raw || !Number.isInteger(depth) || depth < MIN_DEPTH || depth > MAX_DEPTH) {
                    event.target.value = String(context.priorDepth);
                    return;
                  }
                  if (depth !== context.priorDepth) save({ priorDepth: depth });
                }}
                className="w-[52px] rounded-[var(--r-2)] border border-line-strong bg-panel px-[6px] py-[2px] text-right font-mono text-[14px] text-ink-2 outline-none focus:border-accent"
              />
            </label>
          )}
        </>
      )}
    </div>
  );
}
